import { useState, useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Library,
  Search,
  X,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Plus,
  Github,
} from 'lucide-react';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import {
  useListSkillTemplates,
  useInstallTemplate,
  useListAgentSkills,
} from '../../../../api/agent-skills';
import type { SkillTemplate } from '../../../../api/agent-skills';
import { useIsAdmin } from '../../../../components/onboarding-new/api';
import { ScopePickerDialog } from './ScopePickerDialog';
import { SkillsShell } from './SkillsShell';
import { TEMPLATE_CATEGORY_META } from '../utils/constants';
import { encodeSkillRef } from '../utils/skillRef';
import { getApiErrorMessage } from '../../../../lib/utils';
import { getTagColor } from '../utils/tagColors';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

type CategoryFilter = SkillTemplate['category'] | 'all';

const categoryTabs = (
  t: TFunction<'pleri/skills'>,
): { value: CategoryFilter; label: string }[] => [
  { value: 'all', label: t('all') },
  { value: 'security', label: t('common:security') },
  { value: 'compliance', label: t('compliance') },
  { value: 'reporting', label: t('reporting') },
  { value: 'devops', label: 'DevOps' },
  { value: 'general', label: t('general') },
];

/* ------------------------------------------------------------------ */
/* LibraryCard                                                          */
/* ------------------------------------------------------------------ */

interface LibraryCardProps {
  template: SkillTemplate;
  isInstalled: boolean;
  installedSkillRef?: string;
  isInstalling: boolean;
  onAdd: () => void;
  onPreview: () => void;
}

function LibraryCard({
  template,
  isInstalled,
  installedSkillRef,
  isInstalling,
  onAdd,
  onPreview,
}: LibraryCardProps) {
  const { t } = useTranslation('pleri/skills');
  const meta =
    TEMPLATE_CATEGORY_META[template.category] ?? TEMPLATE_CATEGORY_META.general;
  const Icon = meta.icon;
  const tags = template.tags ?? [];

  return (
    <div
      className={`group flex flex-col rounded-2xl border bg-white transition-all duration-200 dark:bg-gray-900 ${
        isInstalled
          ? 'border-gray-200 dark:border-gray-800'
          : 'border-gray-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg dark:border-gray-800 dark:hover:border-gray-700'
      }`}
    >
      {/* Top: icon + name + installed badge */}
      <div className="p-4 pb-2">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl shadow-sm ${meta.bg}`}
          >
            <Icon className={`h-5 w-5 ${meta.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold leading-relaxed text-gray-900 dark:text-gray-100">
                {template.displayName}
              </h3>
              {isInstalled && (
                <span className="flex flex-shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  {t('added')}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5">
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${meta.bg} ${meta.color}`}
              >
                {template.category}
              </span>
              {template.source === 'github' && template.githubOwner && template.githubRepo && (
                <span className="flex items-center gap-0.5 text-xs text-gray-400">
                  <Github className="h-2.5 w-2.5" />
                  {template.githubOwner}/{template.githubRepo}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 px-4 pb-1">
          {tags.map((tag) => {
            const c = getTagColor(tag);
            return (
              <span
                key={tag}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${c.card}`}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}
      {/* Description */}
      <div className="flex-1 overflow-hidden px-4 pb-3">
        <p className="line-clamp-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          {template.description}
        </p>
      </div>
      {/* Footer */}
      <div className="mt-auto flex items-center justify-between border-t border-gray-100 px-4 py-2.5 dark:border-gray-800">
        {isInstalled ? (
          installedSkillRef ? (
            <Link
              to="/skills/$skillRef"
              params={{ skillRef: installedSkillRef }}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50"
            >
              <CheckCircle2 className="h-3 w-3" />
              {t('inMySkills')}
            </Link>
          ) : (
            <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle2 className="h-3 w-3" />
              {t('inMySkills')}
            </span>
          )
        ) : (
          <button
            type="button"
            onClick={onAdd}
            disabled={isInstalling}
            className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 active:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isInstalling ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('adding')}
              </>
            ) : (
              <>
                <Plus className="h-3 w-3" />
                {t('addToMySkills')}
              </>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={onPreview}
          className="text-xs text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
        >
          {t('preview')}
        </button>
      </div>
    </div>
  );
}

function LibraryCardSkeleton() {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="p-4 pb-2">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-1/4 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-1.5 overflow-hidden px-4 py-2">
        <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-3 w-5/6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-3 w-4/6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 dark:border-gray-800">
        <div className="h-7 w-28 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-12 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Preview modal                                                        */
/* ------------------------------------------------------------------ */

function PreviewModal({
  template,
  isInstalled,
  installedSkillRef,
  isInstalling,
  onAdd,
  onClose,
}: {
  template: SkillTemplate;
  isInstalled: boolean;
  installedSkillRef?: string;
  isInstalling: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('pleri/skills');
  const meta =
    TEMPLATE_CATEGORY_META[template.category] ?? TEMPLATE_CATEGORY_META.general;
  const Icon = meta.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-start gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <div
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl shadow-sm ${meta.bg}`}
          >
            <Icon className={`h-4 w-4 ${meta.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {template.displayName}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {template.description}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Instructions */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <ChatMarkdown content={template.instructions} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 px-5 py-3 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            {t('close')}
          </button>
          {isInstalled ? (
            installedSkillRef ? (
              <Link
                to="/skills/$skillRef"
                params={{ skillRef: installedSkillRef }}
                onClick={onClose}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400"
              >
                <CheckCircle2 className="h-3 w-3" />
                {t('viewInMySkills')}
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />
                {t('inMySkills')}
              </span>
            )
          ) : (
            <button
              type="button"
              onClick={onAdd}
              disabled={isInstalling}
              className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isInstalling ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('adding')}
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3" />
                  {t('addToMySkills')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SkillLibraryPage                                                     */
/* ------------------------------------------------------------------ */

export function SkillLibraryPage() {
  const { t } = useTranslation('pleri/skills');
  const navigate = useNavigate();
  const {
    data: templates,
    isLoading,
    isError,
    refetch,
  } = useListSkillTemplates();
  const { data: mySkills } = useListAgentSkills();
  const installTemplate = useInstallTemplate();
  const isAdmin = useIsAdmin();

  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<SkillTemplate | null>(null);
  const [scopePickerTemplate, setScopePickerTemplate] = useState<SkillTemplate | null>(null);

  // Map template name → encoded skill ref for installed skills (user + tenant)
  const installedMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const skill of mySkills ?? []) {
      if (skill.scope === 'user' || skill.scope === 'tenant') {
        if (!map.has(skill.name)) {
          map.set(skill.name, encodeSkillRef(skill.scope, skill.name));
        }
      }
    }
    return map;
  }, [mySkills]);

  const filtered = useMemo(() => {
    let list = templates ?? [];
    if (categoryFilter !== 'all') {
      list = list.filter((t) => t.category === categoryFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.displayName.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q),
      );
    }
    return list;
  }, [templates, categoryFilter, searchQuery]);

  const handleAdd = (template: SkillTemplate) => {
    if (isAdmin) {
      setScopePickerTemplate(template);
    } else {
      doInstall(template, 'user');
    }
  };

  const doInstall = async (template: SkillTemplate, scope: 'user' | 'tenant') => {
    setScopePickerTemplate(null);
    setInstallingId(template.id);
    setInstallError(null);
    try {
      const result = await installTemplate.mutateAsync({ templateId: template.id, scope });
      navigate({
        to: '/skills/$skillRef',
        params: { skillRef: encodeSkillRef(result.scope, result.name) },
      });
    } catch (err) {
      setInstallError(getApiErrorMessage(err, 'Failed to add skill'));
    } finally {
      setInstallingId(null);
    }
  };

  const totalCount = (templates ?? []).length;

  return (
    <div className="flex h-full w-full flex-col bg-gray-50 dark:bg-gray-950">
      {/* Scope picker dialog */}
      {scopePickerTemplate && (
        <ScopePickerDialog
          templateName={scopePickerTemplate.displayName}
          onConfirm={(scope) => doInstall(scopePickerTemplate, scope)}
          onCancel={() => setScopePickerTemplate(null)}
          isLoading={installingId === scopePickerTemplate.id}
        />
      )}
      {/* Preview modal */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          isInstalled={installedMap.has(previewTemplate.name)}
          installedSkillRef={installedMap.get(previewTemplate.name)}
          isInstalling={installingId === previewTemplate.id}
          onAdd={() => handleAdd(previewTemplate)}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
      <SkillsShell activeTab="library" libraryCount={totalCount}>
        <div className="flex items-center gap-3 border-t border-gray-100 px-6 py-3 dark:border-gray-800">
          <div className="relative max-w-xs flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchLibrary')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pr-8 pl-8 text-sm placeholder-gray-400 focus:border-orange-400 focus:bg-white focus:ring-1 focus:ring-orange-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            {categoryTabs(t).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setCategoryFilter(tab.value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  categoryFilter === tab.value
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </SkillsShell>
      {/* Error banner */}
      {installError && (
        <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{installError}</span>
          <button
            type="button"
            onClick={() => setInstallError(null)}
            className="text-red-400 hover:text-red-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {!isLoading && filtered.length > 0 && (
          <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
            {filtered.length === totalCount
              ? t('skillsInTheLibrary', { count: totalCount })
              : t('showingOfSkills', {
                  shown: filtered.length,
                  count: totalCount,
                })}
          </p>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <LibraryCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <AlertTriangle className="mb-3 h-10 w-10 text-red-300" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('failedToLoadSkillLibrary')}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
            >
              {t('tryAgain')}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Library className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {searchQuery
                ? t('noSkillsMatchYourSearch')
                : t('noSkillsInThisCategoryYet')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((template) => {
              const isInstalled = installedMap.has(template.name);
              const installedRef = installedMap.get(template.name);
              return (
                <LibraryCard
                  key={template.id}
                  template={template}
                  isInstalled={isInstalled}
                  installedSkillRef={installedRef}
                  isInstalling={installingId === template.id}
                  onAdd={() => handleAdd(template)}
                  onPreview={() => setPreviewTemplate(template)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
